/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { POPriorityOptions, POPriorityOptionsDict } from 'volta/models/planning-order';
import { translateFilterOptions } from 'volta/utils/filters-utils';

import type { IntlService } from 'ember-intl';
import type { ContentValue, ModifierLike } from '@glint/template';
import type { ILabelAction } from 'volta/components/bv-labelled';
import type { IBvMenuItem } from 'volta/components/bv-menu';
import type { TPOPriorityOption, TPOPriorityOptionValue } from 'volta/models/types/planning-order';
import type { BvFocusUnitModifier } from 'volta/components/bv-focus-zone';
import type { BvSelectArgs } from 'volta/components/bv-select';
import type LabelService from 'volta/services/label-service';

interface PoWorkbenchPrioritySelectSignature {
  Args: {
    autoComponent?: ContentValue;
    stopPropagation?: boolean;
    focusZoneUnit?: BvFocusUnitModifier;
    onSave?: () => void;
    inline?: boolean;
    minimized?: boolean;
    disabled?: boolean;
    renderInPlace?: boolean;
    matchTriggerWidth?: boolean;
    horizontalPosition?: BvSelectArgs['horizontalPosition'];
    allowClear?: boolean;
    labelAction?: ILabelAction;
    label?: string;
    fill?: boolean;
    isLoading?: boolean;
    multiple?: boolean;
    value?: TPOPriorityOptionValue | null;
    values?: TPOPriorityOptionValue[];
    displayClassNames?: string;
    onChange?: (value: TPOPriorityOptionValue) => void;
    onChangeMany?: (values: TPOPriorityOptionValue[]) => void;
    enableFocusTrap?: boolean;
  };
  Blocks: {
    trigger: [unknown];
  };
  Element: HTMLElement;
}

export default class PoWorkbenchPrioritySelectComponent extends Component<PoWorkbenchPrioritySelectSignature> {
  @service intl!: IntlService;
  @service labelService!: LabelService;

  options = translateFilterOptions(POPriorityOptions, this.labelService);
  optionsByValue = POPriorityOptionsDict;

  get selectedOption() {
    const { value } = this.args;
    return isPresent(value) ? POPriorityOptionsDict[`${value}`] : undefined;
  }

  get selectedOptions() {
    const { values = [] } = this.args;
    const valuesAsStr = new Set(values.map((v) => v.toString()));
    return values.length > 0 ? this.options.filter((o) => valuesAsStr.has(o.value.toString())) : [];
  }

  getOptions = (keys: string[], afterAction?: () => void) =>
    POPriorityOptions.reduce<IBvMenuItem[]>((acc, { key, icon, iconColor, value }) => {
      if (keys.length > 0 && !keys.includes(key)) {
        return acc;
      }
      acc.push({
        icon,
        iconColor,
        content: this.intl.t(key),
        selected: this.selectedOption?.value === value,
        onClick: async (_event: Event) => {
          await this.args.onChange?.(value);
          afterAction?.();
        }
      });
      return acc;
    }, []);

  handleChange = (option: TPOPriorityOption) => {
    return this.args.onChange?.(option.value);
  };

  handleChangeMany = (values: TPOPriorityOption[]) => {
    return this.args.onChangeMany?.(values.map((v) => v.value));
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'PoWorkbench::PrioritySelect': typeof PoWorkbenchPrioritySelectComponent;
    'po-workbench/priority-select': typeof PoWorkbenchPrioritySelectComponent;
  }
}
